// Generated by GitHub Copilot
import { useAuthError } from "../contexts/AuthErrorContext";

/**
 * Custom fetch wrapper that handles 401 errors globally
 * Automatically triggers authentication error handling and prevents further processing
 *
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @param {boolean} skipAuthCheck - If true, skips 401 handling (for login endpoints)
 * @returns {Promise<Response>} Fetch response
 * @throws {Error} Throws error for non-401 errors or if authentication check is skipped
 */
export const authFetch = async (url, options = {}, skipAuthCheck = false) => {
  try {
    const response = await fetch(url, {
      credentials: "include",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

    // Check for 401 Unauthorized
    if (response.status === 401 && !skipAuthCheck) {
      // Trigger global auth error handler
      const event = new CustomEvent("auth-error-401");
      window.dispatchEvent(event);

      // Throw error to prevent further processing
      const error = new Error("Unauthorized - Session expired");
      error.status = 401;
      throw error;
    }

    return response;
  } catch (error) {
    // Re-throw the error to be handled by the caller
    throw error;
  }
};

/**
 * Hook that returns an auth-aware fetch function
 * Uses the AuthErrorContext to handle 401 errors with toast notifications
 *
 * @returns {Function} Auth-aware fetch function
 *
 * @example
 * const fetchWithAuth = useAuthFetch();
 * try {
 *   const response = await fetchWithAuth('/api/v1/problems');
 *   const data = await response.json();
 * } catch (error) {
 *   // 401 errors are already handled globally
 *   // Handle other errors here
 * }
 */
export const useAuthFetch = () => {
  const { handleAuthError } = useAuthError();

  return async (url, options = {}, skipAuthCheck = false) => {
    try {
      const response = await fetch(url, {
        credentials: "include",
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
      });

      // Check for 401 Unauthorized
      if (response.status === 401 && !skipAuthCheck) {
        handleAuthError();

        // Throw error to prevent further processing
        const error = new Error("Unauthorized - Session expired");
        error.status = 401;
        throw error;
      }

      return response;
    } catch (error) {
      // If it's a 401 error, it's already been handled
      // Re-throw all errors for the caller to handle
      throw error;
    }
  };
};
